/* lm-archive — hover/focus an entry → its image cross-fades into the right preview pane and the
   row darkens. Single swapping <img>, decode()-preloaded, opacity cross-fade. Rebuilt clean
   (no Webflow IX2). Idempotent; supports theme-editor section reloads. */
(() => {
  if (window.__lmArchive) return;
  window.__lmArchive = true;

  const wire = (root) => {
    if (root.__lmWired) return;
    root.__lmWired = true;
    const list = root.querySelector('[data-archive-list]');
    const preview = root.querySelector('.lm-archive__preview');
    if (!list || !preview) return;
    let activeRow = null;

    const show = (row) => {
      const src = row.dataset.preview;
      if (!src || row === activeRow) return;
      if (activeRow) activeRow.classList.remove('is-active');
      activeRow = row;
      row.classList.add('is-active');
      // set the in-DOM preview directly; CSS opacity transition handles the cross-fade
      if (row.dataset.srcset) preview.srcset = row.dataset.srcset; else preview.removeAttribute('srcset');
      preview.src = src;
      preview.alt = row.dataset.alt || '';
      root.classList.add('is-previewing');
    };

    const hide = () => {
      if (activeRow) activeRow.classList.remove('is-active');
      activeRow = null;
      root.classList.remove('is-previewing');
    };

    // hover/leave only on real pointers — on touch the scroll path (wireScroll) drives the preview
    const hoverable = () => window.matchMedia('(hover: hover)').matches;
    list.addEventListener('pointerover', (e) => { if (!hoverable()) return; const r = e.target.closest('[data-archive-row]'); if (r) show(r); });
    list.addEventListener('focusin', (e) => { const r = e.target.closest('[data-archive-row]'); if (r) show(r); });
    list.addEventListener('pointerleave', () => { if (hoverable()) hide(); });
  };

  // Mobile (touch): no hover, so drive the preview by scroll position. The list is a small window;
  // whatever row sits on the fixed activation line previews above + darkens. rAF-throttled.
  const wireScroll = (root) => {
    if (root.__lmScrollWired) return;
    root.__lmScrollWired = true;
    const mq = window.matchMedia('(max-width: 767px)');
    const list = root.querySelector('.lm-archive__list');
    const line = root.querySelector('.lm-archive__line');
    const preview = root.querySelector('.lm-archive__preview');
    if (!list || !line || !preview) return;
    const rows = () => root.querySelectorAll('[data-archive-row]');
    let raf = 0, active = null, on = false;

    const set = (row) => {
      if (!row || row === active) return;
      const src = row.dataset.preview;
      if (!src) return;
      if (active) active.classList.remove('is-active');
      active = row;
      row.classList.add('is-active');
      if (row.dataset.srcset) preview.srcset = row.dataset.srcset; else preview.removeAttribute('srcset');
      preview.src = src;
      preview.alt = row.dataset.alt || '';
      root.classList.add('is-previewing');
    };

    const pick = () => {
      raf = 0;
      const lr = line.getBoundingClientRect();
      const lineY = lr.top + lr.height / 2;
      let best = null, bestD = Infinity;
      rows().forEach((r) => {
        const rect = r.getBoundingClientRect();
        if (!rect.height) return;
        const d = Math.abs(rect.top + rect.height / 2 - lineY);
        if (d < bestD) { bestD = d; best = r; }
      });
      set(best);
    };
    // the box rides at a resting height while scrolling, then expands to fit the locked-in row once
    // the snap settles (forcing it mid-flight fights the scroll). Collapses again the moment you move.
    const REST = 36;   // resting box while scrolling ≈ a padded single-line row
    let settleTimer = 0, curH = REST;
    // resize ONLY on settle (never mid-scroll — that fights the snap and re-triggers the transition
    // every event so it never finishes). The rows carry their own vertical padding (see lm-archive.css
    // mobile), so the border just wraps the row box EXACTLY: the air is the row's padding and the box
    // never overhangs into the neighbouring entry (1-, 2- and 3-line rows all fit cleanly). Direct
    // height, since Chrome won't transition a var()-backed length.
    const setH = (h) => { h = Math.max(REST, Math.round(h)); if (h === curH) return; curH = h; line.style.height = h + 'px'; };
    const fit = () => { if (active) setH(active.getBoundingClientRect().height); };
    const settle = () => { pick(); fit(); };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(pick);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 140);             // fit the locked-in row once scrolling stops
    };

    // align the snap target with the activation line, and add trailing space so the FIRST and LAST
    // entries can both scroll onto the line (otherwise the last rows never reach a top-anchored line).
    const layout = () => {
      const lr = line.getBoundingClientRect();
      const sr = list.getBoundingClientRect();
      const rel = (lr.top + lr.height / 2) - sr.top;          // line centre within the scroller
      const first = root.querySelector('[data-archive-row]');
      const rowH = first ? first.getBoundingClientRect().height : 20;
      list.style.scrollPaddingBottom = Math.max(0, Math.round(sr.height - 2 * rel)) + 'px';
      list.style.paddingBottom = Math.max(0, Math.round(sr.height - rel - rowH / 2)) + 'px';
    };

    const enable = () => {
      if (on) return;
      on = true;
      list.addEventListener('scroll', onScroll, { passive: true });
      layout();
      pick();
      fit();                                            // fit the first locked-in row on load
    };
    const disable = () => {
      if (!on) return;
      on = false;
      list.removeEventListener('scroll', onScroll);
      clearTimeout(settleTimer);
      if (active) { active.classList.remove('is-active'); active = null; }
      root.classList.remove('is-previewing');
      list.style.scrollPaddingBottom = '';
      list.style.paddingBottom = '';
      line.style.height = '';
      curH = REST;
    };
    const sync = () => { mq.matches ? enable() : disable(); };
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync); else mq.addListener(sync);
    window.addEventListener('resize', () => { if (on) { layout(); onScroll(); } }, { passive: true });
  };

  // Fixed-window page: lock page scroll + pin the footer (image runs behind it) at every breakpoint.
  // Measure the footer + the fixed nav so CSS can reserve footer space and pin the column header
  // just below the nav on mobile. Re-measured on resize (heights change per breakpoint).
  const setupPage = () => {
    if (!document.querySelector('.lm-archive')) return;
    document.documentElement.classList.add('lm-archive-page');
    const setVars = () => {
      const f = document.querySelector('.footer') || document.querySelector('footer');
      if (f) document.documentElement.style.setProperty('--lm-footer-h', Math.round(f.getBoundingClientRect().height) + 'px');
      const nav = document.querySelector('.nav');
      if (nav) document.documentElement.style.setProperty('--lm-nav-h', Math.round(nav.getBoundingClientRect().height) + 'px');
    };
    setVars();
    window.addEventListener('resize', setVars, { passive: true });
  };

  const wireAll = (root) => { wire(root); wireScroll(root); };
  const init = () => { setupPage(); document.querySelectorAll('.lm-archive').forEach(wireAll); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelectorAll) e.target.querySelectorAll('.lm-archive').forEach(wireAll);
  });
})();
